// ===========================================================================
// Shared — patientDetailService.
//
// Trae el detalle clínico del paciente YA seleccionado para alimentar la
// tarjeta de tabs (PatientDetailCard): care gaps y coding opportunities del
// año en curso. La demografía no necesita servicio: ya viene en la ficha que
// entrega PatientSearch.onSelect.
//
// Se apoya en los servicios productivos existentes (CareGapsV2 y
// CodingOpportunities) y normaliza ambas respuestas al shape { items, count }.
//
// PROVISIONAL/AISLADO: igual que patientSearchService, este es el ÚNICO punto a
// tocar si backend entrega endpoints dedicados de "detalle de paciente". Los
// componentes consumen el shape normalizado, no los servicios directos.
// ===========================================================================
import { getCareGaps } from "../../../utils/services/CareGapsV2/careGapsV2Service";
import getCodingOpportunities from "../../../utils/services/CodingOpportunities/getCodingOpportunities";
import { getPatientMembership } from "../../../utils/services/Patients/getPatientMembership";

// Tope de filas a traer por paciente (un paciente nunca tendrá cientos).
const PAGE_SIZE = 200;

/** Año en curso (measurement/measure year por defecto). */
export const currentYear = () => new Date().getFullYear();

/**
 * Care gaps de UN paciente para un año (default: año en curso).
 * Envuelve `getCareGaps({ cipId, measurementYear })` de CareGapsV2.
 *
 * @param {Object} params
 * @param {string} params.cipId              - CIP del paciente seleccionado
 * @param {number} [params.year]             - measurementYear (default año actual)
 *
 * Ejemplo de llamada interna:
 * GET /api/CareGapsV2?cipId=18750327012&measurementYear=2026&page=1&pageSize=200
 *
 * Respuesta (normalizada por este servicio):
 * {
 *   "items": [
 *     {
 *       "careGapId": 1234,
 *       "careGapName": "Breast Cancer Screening",
 *       "measurementYear": 2026,
 *       "careGapStatusName": "Open", "careGapStatusKey": "open",
 *       "workflowStatusName": "New - Not reviewed",
 *       "hedisCategory": "cancer_screening",
 *       "historicallyNonCompliant": true,
 *       "lastSeen": "2026-05-08T06:00:00"
 *     }
 *   ],
 *   "count": 1
 * }
 *
 * @returns {Promise<{items: Array, count: number}>}
 */
export const getPatientCareGaps = async ({ cipId, year } = {}) => {
  if (!cipId) return { items: [], count: 0 };
  const res = await getCareGaps({
    cipId,
    measurementYear: year ?? currentYear(),
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const items = Array.isArray(res?.data) ? res.data : [];
  return { items, count: items.length };
};

/**
 * Coding opportunities (HCC) de UN paciente.
 * Envuelve `getCodingOpportunities({ cipId })`.
 *
 * IMPORTANTE: NO se filtra por `measureYear`. El measure year de las coding
 * opportunities (HCC) no coincide necesariamente con el año calendario, así que
 * forzar `measureYear=añoActual` devolvía vacío para pacientes que sí tienen
 * coding opportunities. Se replica el patrón productivo de
 * `CodingOpContext.loadCodingOpportunitiesByPatient` (trae por cipId sin año) y
 * se expone el `measureYear` de cada item en la UI. Si `year` se pasa explícito,
 * sí se envía como filtro.
 *
 * @param {Object} params
 * @param {string} params.cipId              - CIP del paciente seleccionado
 * @param {number} [params.year]             - measureYear opcional. Si se omite, NO se filtra por año.
 *
 * Ejemplo de llamada interna:
 * GET /api/CodingOpportunities?cipId=18750327012&page=1&pageSize=200
 *
 * Respuesta (normalizada por este servicio):
 * {
 *   "items": [
 *     {
 *       "codingOpportunityId": 987,
 *       "hccId": "HCC18",
 *       "hccDescription": "Diabetes with Chronic Complications",
 *       "diagnosisCode": "E1122",
 *       "diagnosisDescription": "Type 2 diabetes mellitus with diabetic nephropathy",
 *       "measureYear": 2025,
 *       "statusName": "Open",
 *       "lastDos": "2025-11-03T00:00:00"
 *     }
 *   ],
 *   "count": 1
 * }
 *
 * @returns {Promise<{items: Array, count: number}>}
 */
export const getPatientCodingOpportunities = async ({ cipId, year } = {}) => {
  if (!cipId) return { items: [], count: 0 };
  const res = await getCodingOpportunities({
    cipId,
    measureYear: year ?? undefined, // sin año => sin filtro (igual que el módulo productivo)
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const items = Array.isArray(res?.data) ? res.data : [];
  // El backend sí devuelve `total`; úsalo si viene, si no cae al length.
  const count = Number.isFinite(res?.total) ? res.total : items.length;
  return { items, count };
};

// Convención de estado del endpoint genérico de membresías:
// status 0 / "0" / "ACTIVE" => activa, lo demás inactiva (igual que PatientInfoPage).
const membershipIsActive = (status) =>
  status === 0 || status === "0" || status === "ACTIVE";

/**
 * Membresías (pólizas de seguro) de UN paciente. Un paciente puede tener varias
 * (ACA y/o MED). Envuelve `getPatientMembership(cipId)` → GET /api/PatientInfo/Membership.
 *
 * El endpoint genérico devuelve nombres de campo con varios alias; este servicio
 * los normaliza a un shape estable (mismos fallbacks que la tarjeta de membresías
 * de PatientInfoPage, que es código ya probado contra el backend real).
 *
 * @param {Object} params
 * @param {string} params.cipId
 *
 * Ejemplo de llamada interna:
 * GET /api/PatientInfo/Membership?cipId=43750135999
 *
 * Respuesta (normalizada por este servicio):
 * {
 *   "items": [
 *     {
 *       "id": "POL-5678",
 *       "memberType": "ACA",
 *       "memberId": "002W24287",
 *       "plan": "Gold PPO 1500",
 *       "network": "Blue Cross PPO",
 *       "effectiveDate": "2026-01-01T00:00:00",
 *       "isActive": true,
 *       "statusLabel": "Active"
 *     }
 *   ],
 *   "count": 1
 * }
 *
 * @returns {Promise<{items: Array, count: number}>}
 */
export const getPatientMemberships = async ({ cipId } = {}) => {
  if (!cipId) return { items: [], count: 0 };
  const res = await getPatientMembership(cipId);
  const raw = Array.isArray(res) ? res : res ? [res] : [];

  const items = raw.map((m, idx) => {
    const active = membershipIsActive(m.status ?? m.numStatus);
    return {
      id: m.membershipId || m.memberPolicyId || m.id || idx,
      memberType: m.memberType || m.varMemberType || null,
      memberId: m.memberId || m.varMemberId || null,
      plan: m.varMemberPlan || m.memberPlan || m.planName || null,
      network: m.varNetworkName || m.networkName || m.network || null,
      effectiveDate:
        m.datMemberPlanEffectiveDate || m.memberPlanEffectiveDate || m.effectiveDate || null,
      isActive: active,
      statusLabel: active ? "Active" : "Inactive",
    };
  });

  return { items, count: items.length };
};
